import { z } from "zod";
const calendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const parsed = new Date(value + "T00:00:00Z");
    return (
      Number.isFinite(parsed.getTime()) &&
      parsed.toISOString().slice(0, 10) === value
    );
  });
export const FinanceAgingSchema = z.enum([
  "ALL",
  "NOT_DUE",
  "DAYS_1_30",
  "DAYS_31_60",
  "DAYS_61_90",
  "DAYS_91_PLUS",
  "UNDATED",
]);
export const FinanceBalanceQuerySchema = z
  .object({
    q: z.string().trim().max(200).default(""),
    direction: z.enum(["OUTBOUND", "INBOUND"]).default("OUTBOUND"),
    state: z
      .enum(["ALL", "OPEN", "CREDIT", "SETTLED", "REVIEW"])
      .default("ALL"),
    aging: FinanceAgingSchema.default("ALL"),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    limit: z.number().int().min(1).max(100).default(25),
    offset: z.number().int().min(0).max(100000).default(0),
  })
  .strict();
export type FinanceBalanceQuery = z.infer<typeof FinanceBalanceQuerySchema>;
export const FinanceBalanceReasonSchema = z.enum([
  "NO_LEDGER",
  "ESTIMATED",
  "CURRENCY",
  "PRIMARY_AMOUNT",
  "ENTRY_STATUS",
  "DOCUMENT_STATUS",
  "INVALID_AMOUNT",
]);
export const FinanceBalanceRecordSchema = z.object({
  documentId: z.string().min(1),
  documentNumber: z.string().nullable(),
  counterparty: z.string().nullable(),
  status: z.string(),
  direction: z.enum(["INBOUND", "OUTBOUND"]),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullable(),
  dueDate: calendarDate.nullable(),
  originalAmount: z.number().finite().nullable(),
  offsetAmount: z.number().finite().nullable(),
  balance: z.number().finite().nullable(),
  state: z.enum(["OPEN", "CREDIT", "SETTLED", "REVIEW"]),
  reason: FinanceBalanceReasonSchema.nullable(),
  aging: FinanceAgingSchema.exclude(["ALL"]),
  daysOverdue: z.number().int().nonnegative().nullable(),
});
export type FinanceBalanceRecord = z.infer<typeof FinanceBalanceRecordSchema>;
export const FinanceBalanceSummarySchema = z.object({
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullable(),
  aging: FinanceAgingSchema.exclude(["ALL"]),
  count: z.number().int().positive(),
  reviewCount: z.number().int().nonnegative(),
  recordedBalance: z.number().finite().nullable(),
});
export const FinanceBalancePageSchema = z.object({
  tenantId: z.string().min(1),
  asOf: z.string().datetime(),
  scope: FinanceBalanceQuerySchema,
  data: z.array(FinanceBalanceRecordSchema).max(100),
  summary: z.array(FinanceBalanceSummarySchema),
  pagination: z.object({
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
  }),
});
export type FinanceBalancePage = z.infer<typeof FinanceBalancePageSchema>;
